import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Box, HStack, Avatar, Text, Textarea, Portal } from "@chakra-ui/react";

const API_BASE = "https://api.globalwineinstitute.org";
const HEADERS = { headers: { "X-API-KEY": "f3bb4afa55070cc052d1b888d4f7465a" } };

let cachedUsers = null;
let fetchPromise = null;

// The query being typed after "@": letters of any alphabet, so "Piñ" and
// "Magalhã" keep the picker open, plus one space so a surname can narrow it.
// Must follow a space or the start, so an email address never opens it.
const QUERY_RE = /(?:^|\s)@([^\s@]*(?: [^\s@]*)?)$/;

/**
 * Every member as {id, name, firstName, lastName, avatar, organization},
 * fetched once per page load and shared with the feed, which uses the same
 * list to turn "@First Last" in a body into a link.
 */
export const loadUsers = () => {
  if (cachedUsers) return Promise.resolve(cachedUsers);
  if (fetchPromise) return fetchPromise;
  fetchPromise = fetch(`${API_BASE}/wineries/auth.users`, HEADERS)
    .then((r) => (r.ok ? r.json() : []))
    .then((data) => {
      const list = Array.isArray(data) ? data : [];
      cachedUsers = list.map((u) => {
        const first = (u.first_name || "").trim();
        const last = (u.last_name || "").trim();
        const full = `${first} ${last}`.trim() || u.name || u.username || u.email || "Member";
        return {
          id: u.id || u.user_id,
          firstName: first.toLowerCase(),
          lastName: last.toLowerCase(),
          name: full,
          avatar: u.avatar_url || "",
          organization: u.organization || "",
        };
      });
      return cachedUsers;
    })
    .catch(() => {
      cachedUsers = [];
      return cachedUsers;
    });
  return fetchPromise;
};

export const MentionTextarea = ({
  value,
  defaultValue,
  onChange,
  placeholder,
  minH,
  size,
  bg,
  flex,
  rows,
  ...rest
}) => {
  const [users, setUsers] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionStart, setMentionStart] = useState(-1);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const textareaRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    loadUsers().then(setUsers);
  }, []);

  const filtered = useMemo(() => {
    if (mentionQuery === null || mentionQuery.length < 2) return [];
    const q = mentionQuery.toLowerCase();
    return users
      .filter(
        (u) =>
          u.firstName.startsWith(q) ||
          u.lastName.startsWith(q) ||
          u.name.toLowerCase().startsWith(q)
      )
      .slice(0, 8);
  }, [users, mentionQuery]);

  const computeMenuPosition = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMenuPos({
      top: rect.bottom + 4,
      left: rect.left,
    });
  }, []);

  const handleInput = useCallback(
    (e) => {
      const text = e.target.value;
      const cursor = e.target.selectionStart;

      if (onChange) onChange(e);

      const before = text.slice(0, cursor);
      const atMatch = before.match(QUERY_RE);
      if (atMatch) {
        setMentionQuery(atMatch[1]);
        // The "@" sits right before the query; the match may also hold the
        // space in front of it, which stays.
        setMentionStart(cursor - atMatch[1].length - 1);
        setSelectedIdx(0);
        computeMenuPosition();
      } else {
        setMentionQuery(null);
        setMentionStart(-1);
      }
    },
    [onChange, computeMenuPosition]
  );

  const insertMention = useCallback(
    (user) => {
      const el = textareaRef.current;
      if (!el) return;
      const current = el.value;
      const before = current.slice(0, mentionStart);
      const after = current.slice(el.selectionStart);
      const mention = `@${user.name} `;
      const newValue = before + mention + after;

      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLTextAreaElement.prototype,
        "value"
      ).set;
      nativeInputValueSetter.call(el, newValue);
      el.dispatchEvent(new Event("input", { bubbles: true }));

      const cursorPos = before.length + mention.length;
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(cursorPos, cursorPos);
      });

      setMentionQuery(null);
      setMentionStart(-1);
    },
    [mentionStart]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (mentionQuery === null || mentionQuery.length < 2 || filtered.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIdx((i) => (i + 1) % filtered.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIdx((i) => (i - 1 + filtered.length) % filtered.length);
      } else if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        insertMention(filtered[selectedIdx]);
      } else if (e.key === "Escape") {
        setMentionQuery(null);
      }
    },
    [mentionQuery, filtered, selectedIdx, insertMention]
  );

  const isOpen = mentionQuery !== null && filtered.length > 0;

  return (
    <Box position="relative" flex={flex} w="100%">
      <Textarea
        ref={textareaRef}
        value={value}
        defaultValue={defaultValue}
        onChange={handleInput}
        onKeyDown={(e) => {
          handleKeyDown(e);
          if (rest.onKeyDown) rest.onKeyDown(e);
        }}
        placeholder={placeholder}
        minH={minH}
        size={size}
        bg={bg}
        rows={rows}
        {...rest}
      />
      {isOpen && (
        <Portal>
          <Box
            ref={menuRef}
            position="fixed"
            top={`${menuPos.top}px`}
            left={`${menuPos.left}px`}
            w="280px"
            maxH="240px"
            overflowY="auto"
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            boxShadow="lg"
            zIndex={9999}
            py={1}
          >
            {filtered.map((u, idx) => (
              <HStack
                key={u.id}
                px={3}
                py={2}
                cursor="pointer"
                bg={idx === selectedIdx ? "gwbi.50" : "white"}
                _hover={{ bg: "gwbi.50" }}
                onClick={() => insertMention(u)}
                spacing={2}
              >
                <Avatar size="xs" name={u.name} src={u.avatar || undefined} />
                <Box overflow="hidden">
                  <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                    {u.name}
                  </Text>
                  {u.organization && (
                    <Text fontSize="xs" color="gray.500" noOfLines={1}>
                      {u.organization}
                    </Text>
                  )}
                </Box>
              </HStack>
            ))}
          </Box>
        </Portal>
      )}
    </Box>
  );
};
